import { validateNotNullishObject } from "@faro-lotv/foundation";
import {
  Matrix4,
  Matrix4Tuple,
  Vector3,
  Vector3Tuple,
  type TextureImageData,
} from "three";

// Define a range in 2D spaces of a bitmap
export type PixelRange = {
  xRangeMin: number;
  yRangeMin: number;
  xRangeMax: number;
  yRangeMax: number;
};

/**
 * Test wether the input is a valid TextureImageData object
 *
 * @param imageData object to check
 * @returns a boolean indicating if the input is a valid TextureImageData object
 */
export function isTextureImageData(
  imageData: unknown,
): imageData is TextureImageData {
  return (
    validateNotNullishObject(imageData, "TextureImageData") &&
    typeof imageData.width === "number" &&
    typeof imageData.height === "number" &&
    typeof imageData.data === "object" &&
    (imageData.data instanceof Uint8ClampedArray ||
      imageData.data instanceof Uint8Array) &&
    imageData.data.length === imageData.width * imageData.height * 4
  );
}

// Maximum value for a color component in RGBA format
const MAX_COMPONENT_VALUE = 255;

/**
 * Utility class to extract the core of an image, i.e. the area of interest, ignoring useless blank area
 * based on scan intensity (darker pixels = high scan density are assumed to be of higher interest)
 * and outlier detection (high scan density pixels surrounded by lower scan density pixels are assumed to be outliers and are ignored).
 * This utility can typically be used for extracting from a overview map of a scan the area when most scans points are located (
 * assuming the background is transparent or white, and scan points are black or solid colors).
 */
export class ImageCoreExtractor {
  /**
   * @returns "intensity" of a pixel in a scan bitmap, from 0 (white or full transparent =  no point)
   * to 255*255 (black and full opaque = lots of points)";
   * This "intensity" is based on inverse lightness of the pixels, scaled by the alpha channel (less opaque = less intensity)
   * @param image TextureImageData storing the bitmap's pixels in RGBA format`
   * @param x x coordinate of the pixel
   * @param y y coordinate of the pixel
   */
  static getScanIntensity(
    image: TextureImageData,
    x: number,
    y: number,
  ): number {
    const index = (y * image.width + x) * 4;
    const alpha = image.data[index + 3];
    const maxComponent = Math.max(
      image.data[index + 0],
      image.data[index + 1],
      image.data[index + 2],
    );
    const minComponent = Math.min(
      image.data[index + 0],
      image.data[index + 1],
      image.data[index + 2],
    );
    const lightness = (maxComponent + minComponent) / 2;
    const scanIntensity = (MAX_COMPONENT_VALUE - lightness) * alpha;
    return scanIntensity;
  }

  // Ratio between the intensity of a pixel and the intensity of all its neighbors for the pixel to be considered as an outlier
  static #outlierRatioThreshold = 0.1;

  /**
   * @returns true is a specific pixel is an outlier = all 8 neighbors scan intensity is lower than this pixel's intensity
   * @param image TextureImageData storing the bitmap's pixels in RGBA format`
   * @param x x coordinate of the pixel
   * @param y y coordinate of the pixel
   * @param intensity optional pre-computed intensity for this pixel
   * DESIGN NOTES
   * pixels whose intensity is 0 are always outliers
   */
  static isOutlier(
    image: TextureImageData,
    x: number,
    y: number,
    intensity?: number,
  ): boolean {
    const pixelIntensity = intensity ?? this.getScanIntensity(image, x, y);
    if (pixelIntensity === 0) return true;
    const outlierIntensityThreshold =
      this.#outlierRatioThreshold * pixelIntensity;
    // compute relative range so we take into account the borders of te image
    const lowX = x === 0 ? 0 : -1;
    const lowY = y === 0 ? 0 : -1;
    const highX = x === image.width - 1 ? 0 : +1;
    const highY = y === image.height - 1 ? 0 : +1;
    // check all neighbors (up to 8), ignoring the pixel itself`
    for (let dy = lowY; dy <= highY; ++dy) {
      for (let dx = lowX; dx <= highX; ++dx) {
        if (dx === 0 && dy === 0) continue;
        const neighborIntensity = this.getScanIntensity(image, x + dx, y + dy);
        if (neighborIntensity > outlierIntensityThreshold) return false;
      }
    }
    return true;
  }

  /**
   * @returns the peak scan intensity of the image (ignoring outlier pixels) if any, and its coordinates;
   * peakIntensity is undefined if no peak is found (e.g. the image is blank or all pixels are outliers)
   * @param image TextureImageData storing the bitmap's pixels in RGBA format
   * @param ignoreOutlier true to ignore outliers (that could be more intense than the peak); false to return the real peak
   */
  static getPeakIntensity(
    image: TextureImageData,
    ignoreOutlier: boolean,
  ): { peakIntensity?: number; xPeak: number; yPeak: number } {
    let peakIntensity: number | undefined = undefined;
    let xPeak = 0;
    let yPeak = 0;
    for (let y = 0; y < image.height; ++y) {
      for (let x = 0; x < image.width; ++x) {
        const intensity = this.getScanIntensity(image, x, y);
        if (
          (peakIntensity === undefined || intensity > peakIntensity) &&
          (!ignoreOutlier || !this.isOutlier(image, x, y, intensity))
        ) {
          peakIntensity = intensity;
          xPeak = x;
          yPeak = y;
        }
      }
    }
    return { peakIntensity, xPeak, yPeak };
  }

  /**
   * @returns the range of the image's core (area of interest, ignoring useless blank area) in pixel coordinates
   * @param image the image to compute the corners for
   */
  static findCoreFromImageLocal(image: TextureImageData): PixelRange {
    const { width, height } = image;

    // First try to get the peak ignoring outliers
    let ignoreOutlier = true;
    let { peakIntensity, xPeak, yPeak } = this.getPeakIntensity(
      image,
      ignoreOutlier,
    );
    // If no peak found, try to get the peak including outliers
    if (peakIntensity === undefined) {
      ignoreOutlier = false;
      ({ peakIntensity, xPeak, yPeak } = this.getPeakIntensity(
        image,
        ignoreOutlier,
      ));
      if (peakIntensity === undefined) {
        // no peak found, return the whole image as core
        return {
          xRangeMin: 0,
          yRangeMin: 0,
          xRangeMax: width - 1,
          yRangeMax: height - 1,
        };
      }
    }

    /** Ratio between a local maximum and neighbor value to be considered as same block  */
    const thresholdMax = 0.4;

    // temporary find the range of pixels with highest intensity
    const thresholdIntensity = thresholdMax * peakIntensity;
    let xRangeMin = width;
    let yRangeMin = height;
    let xRangeMax = 0;
    let yRangeMax = 0;
    for (let y = 0; y < height; ++y) {
      for (let x = 0; x < width; ++x) {
        const intensity = this.getScanIntensity(image, x, y);
        if (intensity >= thresholdIntensity) {
          const isOutlier = ignoreOutlier && this.isOutlier(image, x, y);
          if (isOutlier) {
            // we can save processing time and bypass next pixel as it is lower than the outlier itself
            ++x;
          } else {
            xRangeMin = Math.min(xRangeMin, x);
            yRangeMin = Math.min(yRangeMin, y);
            xRangeMax = Math.max(xRangeMax, x);
            yRangeMax = Math.max(yRangeMax, y);
          }
        }
      }
    }

    // special case when the image is blank with one single pixel
    if (xRangeMin > xRangeMax || yRangeMin > yRangeMax) {
      xRangeMin = xPeak;
      xRangeMax = xPeak;
      yRangeMin = yPeak;
      yRangeMax = yPeak;
    }

    return { xRangeMin, yRangeMin, xRangeMax, yRangeMax };
  }

  /**
   * @returns the 4 corners of the image's core (area of interest, ignoring useless blank area) in world coordinates
   * @param image image to compute the corners for
   * @param widthElement width of the image in meters
   * @param heightElement height of the image in meters
   * @param worldMatrixTuple transformation matrix from image to world coordinates system
   */
  static findCoreFromImage(
    image: TextureImageData,
    widthElement: number,
    heightElement: number,
    worldMatrixTuple: Matrix4Tuple,
  ): Vector3Tuple[] {
    const { xRangeMin, yRangeMin, xRangeMax, yRangeMax } =
      this.findCoreFromImageLocal(image);
    // get the size of the image in pixels
    const { width, height } = image;

    const worldMatrix = new Matrix4().fromArray(worldMatrixTuple);

    const xRatio = widthElement / width;
    const yRatio = heightElement / height;
    // Take care that xRangeMax and yRangeMax are the last pixel of the area = add 1 pixel to get the max coordinates in meters
    return [
      new Vector3(xRangeMin * xRatio, yRangeMin * yRatio, 0),
      new Vector3((xRangeMax + 1) * xRatio, yRangeMin * yRatio, 0),
      new Vector3(xRangeMin * xRatio, (yRangeMax + 1) * yRatio, 0),
      new Vector3((xRangeMax + 1) * xRatio, (yRangeMax + 1) * yRatio, 0),
    ].map((v) => v.applyMatrix4(worldMatrix).toArray());
  }
}
